import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Leaf, BarChart3 } from "lucide-react";

const INGREDIENTS =
  "Cacau em pó 100%, ovos, chocolate 70% cacau zero, farinha de arroz e aveia sem glúten, purê de maçã, óleo de coco, eritritol, colágeno hidrolisado, essência de baunilha, café solúvel, nibs de cacau e sal.";

const NUTRITION = [
  { label: "Valor energético", value: "250 kcal", vd: "13%" },
  { label: "Carboidratos", value: "28 g", vd: "9%" },
  { label: "Açúcares totais", value: "3,5 g", vd: "N/A" },
  { label: "Proteínas", value: "8,5 g", vd: "17%" },
  { label: "Gorduras totais", value: "14 g", vd: "18%" },
  { label: "Gorduras saturadas", value: "9 g", vd: "45%" },
  { label: "Fibras alimentares", value: "7 g", vd: "28%" },
  { label: "Sódio", value: "130 mg", vd: "7%" },
];

function IconButton({ onClick, label, children, testId }) {
  return (
    <span className="relative group/btn inline-flex">
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-label={label}
        className="w-9 h-9 border border-black inline-flex items-center justify-center bg-white text-black hover:bg-black hover:text-white transition-colors"
      >
        {children}
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-9 whitespace-nowrap bg-black text-white text-[10px] uppercase tracking-[0.18em] px-2 py-1 opacity-0 group-hover/btn:opacity-100 transition-opacity"
      >
        {label}
      </span>
    </span>
  );
}

export default function FlavorInfoButtons({ flavor }) {
  const [open, setOpen] = useState(null); // 'ingredients' | 'nutrition' | null

  return (
    <>
      <div className="mt-5 flex items-center gap-2" data-testid={`info-buttons-${flavor.slug}`}>
        <IconButton
          testId={`btn-ingredients-${flavor.slug}`}
          onClick={() => setOpen("ingredients")}
          label="Ingredientes"
        >
          <Leaf size={14} strokeWidth={1.8} />
        </IconButton>
        <IconButton
          testId={`btn-nutrition-${flavor.slug}`}
          onClick={() => setOpen("nutrition")}
          label="Informação nutricional"
        >
          <BarChart3 size={14} strokeWidth={1.8} />
        </IconButton>
      </div>

      <Dialog open={open === "ingredients"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent
          data-testid={`dialog-ingredients-${flavor.slug}`}
          className="max-w-lg bg-white text-black border border-black rounded-none p-8 [&>button]:text-black"
        >
          <DialogHeader>
            <p className="text-xs uppercase tracking-[0.2em]">Ingredientes</p>
            <DialogTitle className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1] mt-2">
              {flavor.label}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Lista de ingredientes do brownie {flavor.label}
            </DialogDescription>
          </DialogHeader>
          <div className="hairline mt-2" />
          <p className="text-sm leading-relaxed mt-4">{INGREDIENTS}</p>
          <p className="mt-6 text-[11px] uppercase tracking-[0.2em]">
            Sem açúcar · Sem glúten · Sem lactose · Com colágeno hidrolisado
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "nutrition"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent
          data-testid={`dialog-nutrition-${flavor.slug}`}
          className="max-w-lg bg-white text-black border border-black rounded-none p-8 [&>button]:text-black"
        >
          <DialogHeader>
            <p className="text-xs uppercase tracking-[0.2em]">Informação nutricional</p>
            <DialogTitle className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1] mt-2">
              {flavor.label}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Tabela nutricional do brownie {flavor.label}, porção de aproximadamente 90 gramas.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 border border-black">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-black">
              <div className="col-span-8">
                <p className="font-display text-base leading-tight">Tabela nutricional</p>
                <p className="text-[11px] uppercase tracking-[0.18em] mt-1">1 porção · ~90 g</p>
              </div>
              <div className="col-span-4 flex items-end justify-end">
                <p className="font-display text-base">% VD (*)</p>
              </div>
            </div>
            <ul>
              {NUTRITION.map((row, i) => (
                <li
                  key={row.label}
                  className={`grid grid-cols-12 px-4 py-3 text-sm ${i < NUTRITION.length - 1 ? "border-b border-black" : ""}`}
                >
                  <span className="col-span-7">{row.label}</span>
                  <span className="col-span-2 text-right tabular-nums">{row.value}</span>
                  <span className="col-span-3 text-right tabular-nums">{row.vd}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-4 text-[11px] leading-relaxed">
            (*) % Valores diários de referência com base em uma dieta de 2.000 kcal ou
            8.400 kJ. Seus valores diários podem ser maiores ou menores dependendo de
            suas necessidades energéticas.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
